import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Upload, FileText as DocIcon, Tag, Trash2, Settings as SettingsIcon, Zap, Mic } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiError, mediaUrl } from '../../../lib/api';

const MAX_FILES = 100;
const MAX_MB = 200;

export const SORTS = [['newest', 'Plus récents'], ['name', 'Nom A–Z'], ['sent', 'Plus envoyés']];

export function UploadDialog({ open, account, allTags, onClose, onCreated }) {
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({ name: '', description: '' });
  const [selTags, setSelTags] = useState([]);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => { if (open) { setFiles([]); setForm({ name: '', description: '' }); setSelTags([]); setTagsOpen(false); setNewTag(''); setProgress(0); } }, [open]);

  const onFiles = (list) => {
    const arr = Array.from(list || []);
    const tooBig = arr.filter(f => f.size > MAX_MB * 1048576);
    if (tooBig.length) toast.error(`${tooBig.length} fichier(s) de plus de ${MAX_MB} Mo ignoré(s)`);
    const ok = arr.filter(f => f.size <= MAX_MB * 1048576).slice(0, MAX_FILES - files.length);
    if (files.length + arr.length > MAX_FILES) toast.error(`Max ${MAX_FILES} médias par upload`);
    setFiles(prev => [...prev, ...ok]);
  };

  const toggleTag = (t) => setSelTags(s => s.includes(t) ? s.filter(x => x !== t) : [...s, t]);
  const detectType = (f) => f.type.startsWith('image') ? 'photo' : f.type.startsWith('video') ? 'video' : f.type.startsWith('audio') ? 'voice' : 'document';

  const save = async () => {
    if (files.length === 0) { toast.error('Sélectionne au moins un fichier'); return; }
    if (!account || account === 'all') { toast.error("Choisis d'abord un compte"); return; }
    setSaving(true);
    let done = 0;
    let failed = 0;
    const baseName = form.name.trim();
    const uploadOne = async (f, idx) => {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post('/vault/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      const name = baseName ? (files.length > 1 ? `${baseName} ${idx + 1}` : baseName) : f.name.replace(/\.[^.]+$/, '');
      await api.post('/vault', {
        account_id: account, name, type: detectType(f),
        tags: selTags, description: form.description,
        url: data.url, size_mb: data.size_mb,
      });
    };
    // Upload 3 files at a time: much faster than one-by-one, without
    // saturating the connection (large videos) or the backend.
    const CONCURRENCY = 3;
    const queue = files.map((f, i) => [f, i]);
    const worker = async () => {
      while (queue.length) {
        const [f, i] = queue.shift();
        try { await uploadOne(f, i); done += 1; } catch { failed += 1; }
        setProgress(done);
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, files.length) }, worker));
    setSaving(false);
    if (failed) toast.error(`${failed}/${files.length} upload(s) échoué(s) — ${done} réussi(s)`);
    else toast.success(`${done} média(s) uploadé(s) dans le vault`);
    if (done > 0) onCreated();
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && !saving && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-md max-h-[85vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><span className="w-1 h-5 bg-blue-500 rounded-full" /> Uploader des médias</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Limite : <b className="text-slate-300">{MAX_FILES} médias</b> max, <b className="text-slate-300">{MAX_MB} Mo</b> par fichier.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><Upload size={12} /> Fichiers <span className="text-rose-400">*</span> <span className="text-slate-500">(max {MAX_FILES})</span></Label>
            <input ref={inputRef} type="file" multiple className="hidden" accept="image/*,video/*,audio/*,.pdf,.txt,.doc,.docx" onChange={e => { onFiles(e.target.files); e.target.value = ''; }} />
            <div className="flex items-center gap-3">
              <Button data-testid="upload-select-files" type="button" onClick={() => inputRef.current?.click()} className="bg-blue-500 hover:bg-blue-600 rounded-full h-9 px-4 text-sm">Choisir des fichiers</Button>
              <span className="text-sm text-slate-400" data-testid="upload-files-count">{files.length === 0 ? 'Aucun fichier sélectionné' : `${files.length} fichier${files.length > 1 ? 's' : ''} sélectionné${files.length > 1 ? 's' : ''}`}</span>
            </div>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1 max-h-24 overflow-y-auto scrollbar-thin">
                {files.map((f, i) => (
                  <span key={`${f.name}-${f.size}-${i}`} className="text-[10px] px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-slate-300 flex items-center gap-1.5">
                    {f.name.length > 22 ? f.name.slice(0, 20) + '…' : f.name}
                    <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400">✕</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><DocIcon size={12} /> Nom</Label>
            <Input data-testid="upload-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" placeholder="Optionnel" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><DocIcon size={12} /> Description</Label>
            <Input data-testid="upload-desc-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" placeholder="Optionnel" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><Tag size={12} /> Tags <span className="text-slate-500 normal-case">(appliqués à tous les médias uploadés)</span></Label>
            <button data-testid="upload-tags-toggle" type="button" onClick={() => setTagsOpen(o => !o)} className="w-full rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 flex items-center justify-between text-sm text-slate-300 hover:bg-white/[0.07]">
              {selTags.length === 0 ? 'Aucun tag sélectionné' : `${selTags.length} tag${selTags.length > 1 ? 's' : ''} sélectionné${selTags.length > 1 ? 's' : ''}`}
              <span className={`transition-transform text-slate-500 ${tagsOpen ? 'rotate-180' : ''}`}>▾</span>
            </button>
            {tagsOpen && (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-2 space-y-0.5 max-h-40 overflow-y-auto scrollbar-thin">
                {allTags.map(t => (
                  <label key={t} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer">
                    <Checkbox data-testid={`upload-tag-${t}`} checked={selTags.includes(t)} onCheckedChange={() => toggleTag(t)} className="border-white/20 data-[state=checked]:bg-blue-500" />
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-sm text-slate-200">{t}</span>
                  </label>
                ))}
                {allTags.length === 0 && <div className="text-[11px] text-slate-600 px-2 py-1">Aucun tag pour le moment — crées-en un ci-dessous.</div>}
                <div className="flex gap-1.5 pt-1.5 border-t border-white/5 mt-1">
                  <Input data-testid="upload-new-tag" value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newTag.trim()) { setSelTags(s => [...s, newTag.trim()]); setNewTag(''); } }} className="bg-white/5 border-white/10 text-white h-8 text-xs" placeholder="Nouveau tag…" />
                  <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-white/10 bg-white/5 text-slate-300" onClick={() => { if (newTag.trim()) { setSelTags(s => [...s, newTag.trim()]); setNewTag(''); } }}>Ajouter</Button>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={onClose} disabled={saving}>Annuler</Button>
            <Button data-testid="vault-save-button" className="bg-blue-500 hover:bg-blue-600" onClick={save} disabled={saving}>
              <Upload size={14} className="mr-1.5" /> {saving ? `Upload ${progress}/${files.length}…` : 'Uploader'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AddTagDialog({ open, count, allTags, tagColors = {}, onClose, onApply, onSelectAll }) {
  const TAG_COLORS = ['#6366f1', '#3b82f6', '#f43f5e', '#f59e0b', '#10b981', '#a78bfa', '#ec4899', '#64748b'];
  const [name, setName] = useState('');
  const [color, setColor] = useState('#6366f1');
  useEffect(() => { if (open) { setName(''); setColor('#6366f1'); } }, [open]);
  const submit = () => { if (name.trim() && count > 0) onApply(name.trim(), color); };
  const noSelection = count === 0;
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><Tag size={15} className="text-indigo-400" /> Ajouter un tag</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            {noSelection
              ? 'Aucun média sélectionné pour le moment. Choisis au moins un média avec les cases rondes, ou applique le tag à tous les médias visibles.'
              : `Le tag sera appliqué aux ${count} média(s) sélectionné(s).`}
          </DialogDescription>
        </DialogHeader>
        {noSelection && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 text-xs text-amber-200 flex items-start gap-2" data-testid="add-tag-no-selection">
            <Tag size={12} className="mt-0.5 shrink-0" />
            <div>
              Aucun média sélectionné. Clique sur <span className="text-white font-semibold">Tout sélectionner</span> ci-dessous, ou ferme cette fenêtre et coche les ronds à côté des médias voulus.
            </div>
          </div>
        )}
        <div className="space-y-3 pt-1">
          <Input data-testid="add-tag-name-input" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submit(); }} autoFocus className="bg-white/5 border-white/10 text-white h-10" placeholder="ex. premium, été…" />
          <div className="space-y-1.5">
            <div className="text-[11px] text-slate-400 font-medium">Couleur du tag</div>
            <div className="flex gap-1.5">
              {TAG_COLORS.map(c => (
                <button key={c} type="button" data-testid={`tag-color-${c.slice(1)}`} onClick={() => setColor(c)}
                  className={`w-6 h-6 rounded-full border-2 transition-transform ${color === c ? 'border-white scale-110' : 'border-white/10 hover:border-white/40'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {allTags.map(t => (
                <button key={t} data-testid={`existing-tag-${t}`} onClick={() => { setName(t); if (tagColors[t]) setColor(tagColors[t]); }} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${name === t ? 'border-indigo-400 bg-indigo-500/20 text-white' : 'border-white/10 bg-white/5 text-slate-400 hover:text-white'}`}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5" style={{ backgroundColor: tagColors[t] || '#64748b' }} />{t}
                </button>
              ))}
            </div>
          )}
          {noSelection && (
            <Button data-testid="add-tag-select-all-button" variant="outline" className="w-full border-white/10 bg-white/5 text-white hover:bg-white/10" onClick={onSelectAll}>Tout sélectionner</Button>
          )}
          <Button data-testid="add-tag-apply-button" className="w-full bg-indigo-500 hover:bg-indigo-600" onClick={submit} disabled={!name.trim() || noSelection}>
            {noSelection ? "Sélectionne d'abord des médias" : `Appliquer à ${count} média(s)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EditDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', tags: '' });
  useEffect(() => { if (item) setForm({ name: item.name, tags: item.tags.join(', ') }); }, [item]);

  const save = async () => {
    try {
      const { data } = await api.patch(`/vault/${item.id}`, {
        name: form.name,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      toast.success('Média mis à jour');
      onSaved(data);
    } catch (e) { toast.error(apiError(e, 'Échec de la mise à jour')); }
  };

  return (
    <Dialog open={!!item} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-sm">
        <DialogHeader><DialogTitle className="text-white">Modifier le média</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          {item?.url && item.type === 'photo' && <img src={mediaUrl(item.url)} alt="" className="w-full h-32 object-cover rounded-xl" />}
          <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Nom</Label><Input data-testid="edit-vault-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" /></div>
          <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Tags</Label><Input data-testid="edit-vault-tags" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" placeholder="premium, summer" /></div>
          <Button data-testid="edit-vault-save" className="w-full bg-indigo-500 hover:bg-indigo-600" onClick={save}>Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function VaultSettingsDialog({ open, onClose, sort, setSort, type, setType, cacheStats, onClearAllCache }) {
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><SettingsIcon size={15} className="text-indigo-400" /> Réglages du Vault</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Ajuste l'affichage par défaut et le cache Telegram.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Tri par défaut</Label>
            <select data-testid="settings-default-sort" value={sort} onChange={e => setSort(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white h-10 outline-none">
              {SORTS.map(([k, l]) => <option key={k} value={k} className="bg-[#0B0B0E]">{l}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Filtre type par défaut</Label>
            <select data-testid="settings-default-type" value={type} onChange={e => setType(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white h-10 outline-none">
              <option value="all" className="bg-[#0B0B0E]">Tous les types</option>
              <option value="photo" className="bg-[#0B0B0E]">Photo</option>
              <option value="video" className="bg-[#0B0B0E]">Vidéo</option>
              <option value="document" className="bg-[#0B0B0E]">Document</option>
              <option value="voice" className="bg-[#0B0B0E]">Vocal</option>
            </select>
          </div>
          <div className="space-y-2 pt-2 border-t border-white/5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><Zap size={12} className="text-emerald-300" /> Cache des file-id Telegram</Label>
            {cacheStats ? (
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-1 text-xs">
                <div className="flex justify-between text-slate-400"><span>Entrées en cache</span><span className="text-white tabular-nums font-semibold" data-testid="settings-cache-entries">{cacheStats.cached_entries}</span></div>
                <div className="flex justify-between text-slate-400"><span>Médias du vault en cache</span><span className="text-white tabular-nums font-semibold">{cacheStats.cached_vault_items}</span></div>
                <div className="flex justify-between text-slate-400"><span>Uploads évités</span><span className="text-emerald-400 tabular-nums font-semibold">{cacheStats.estimated_uploads_saved}</span></div>
                <div className="flex justify-between text-slate-400"><span>Bande passante économisée</span><span className="text-emerald-400 tabular-nums font-semibold">{cacheStats.estimated_mb_saved} Mo</span></div>
              </div>
            ) : (
              <div className="text-xs text-slate-500">Aucune statistique disponible.</div>
            )}
            <Button data-testid="settings-clear-cache-button" variant="outline" className="w-full border-rose-500/30 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10" onClick={onClearAllCache}>
              <Trash2 size={13} className="mr-1.5" /> Vider tous les file-id en cache
            </Button>
            <p className="text-[10px] text-slate-500">Le prochain envoi de chaque média le re-uploadera sur Telegram et reconstruira le cache automatiquement.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function LightboxDialog({ preview, onClose, tagColors }) {
  // Always serve the playable ORIGINAL. For media offloaded to Telegram storage,
  // preview.url is only a thumbnail (or null for audio); /original re-fetches the
  // real file. For non-offloaded items it serves the local file directly.
  const orig = preview?.id ? mediaUrl(`/api/vault/${preview.id}/original`) : mediaUrl(preview?.url);
  return (
    <Dialog open={!!preview} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-black/95 border-white/10 text-white max-w-5xl w-[92vw] p-0 overflow-hidden" data-testid="vault-lightbox">
        <DialogHeader className="px-5 py-3 border-b border-white/10">
          <DialogTitle className="text-white text-base">{preview?.name}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-center bg-black/80 p-4 min-h-[60vh] max-h-[80vh]">
          {preview?.type === 'photo' && <img src={orig} alt={preview.name} className="max-w-full max-h-[75vh] object-contain rounded" />}
          {preview?.type === 'video' && <video src={orig} controls autoPlay className="max-w-full max-h-[75vh] rounded" />}
          {preview?.type === 'voice' && (
            <div className="text-center">
              <Mic size={40} className="text-indigo-400 mx-auto mb-3" />
              <audio src={orig} controls autoPlay />
              <div className="text-sm text-slate-400 mt-3">{preview.name}</div>
            </div>
          )}
          {preview?.type === 'document' && (
            <a href={orig} target="_blank" rel="noreferrer" className="text-indigo-300 underline flex items-center gap-2"><DocIcon size={18} /> Ouvrir le document</a>
          )}
        </div>
        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between text-xs text-slate-400">
          <div className="capitalize">{preview?.type} · {preview?.size_mb > 0 ? `${preview.size_mb} Mo` : `${preview?.sent || 0} envoyé(s)`}</div>
          <div className="flex flex-wrap gap-1">
            {(preview?.tags || []).map(tg => (
              <span key={tg} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: (tagColors[tg] || '#64748b') + '33', color: tagColors[tg] || '#94a3b8', border: `1px solid ${(tagColors[tg] || '#64748b')}66` }}>{tg}</span>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
